import React from 'react'
import { Tag, message } from '@surecompanies/core_components'
import { Popover } from 'antd'
import {
  ClockCircleFilled,
  CopyOutlined,
  ExportOutlined,
} from '@ant-design/icons'
import styled from 'styled-components'
import type { ICellRendererParams } from '@ag-grid-community/core'
import { copyToClipboard } from 'components/CopyLink/CopyText'
import { middleEllipsis } from './VcnManagementBulkUploadModal.helpers'
import { colors, font, radius } from './tokens'

import type { VcnManagementCardRow, VcnManagementBenefitRow } from './types'
import { VirtualCardType } from 'modules/virtual-cards/constants'
import { VcnCardInUseStatus, VcnCardStatus } from './types'
import {
  getStatusPillStyle,
  getVcnCardInUseLabel,
  getVcnCardInUseTone,
  getVcnCardStatusLabel,
  getVcnCardStatusPillStyle,
} from './vcnCardStatus'

const CopyableText = styled.span`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #003146;

  &:hover {
    text-decoration: underline;
  }
`

/** The design sets the VCN-number column in mono so digits line up down the
    column and a transposed pair is visible at a glance. */
const MonoCopyableText = styled(CopyableText)`
  font-family: ${font.mono};
`

const MUTED = '#8c8c8c'

export function BenefitIdCellRenderer(
  params: ICellRendererParams<VcnManagementBenefitRow, string>
) {
  const value = params.value ?? ''
  if (!value) return <span style={{ color: MUTED }}>—</span>

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    void copyToClipboard(value).then((ok) => {
      if (ok) void message.success('Copied to clipboard')
    })
  }

  // Truncated like the Card Mode benefit-id cell: a full UUID overflows the
  // column. `title` keeps the whole id on hover and the copy yields the full
  // value, not the truncated form.
  return (
    <CopyableText onClick={handleCopy} title={value}>
      {middleEllipsis(value)}
      <CopyOutlined style={{ fontSize: 11 }} />
    </CopyableText>
  )
}

/**
 * VCN token cell: click to copy. Tokens overflow the column, so the text is
 * middle-ellipsised and `title` keeps the whole value on hover — the copy
 * always yields the full token, not the truncated form.
 */
export function VcnTokenCellRenderer(
  params: ICellRendererParams<
    VcnManagementBenefitRow | VcnManagementCardRow,
    string
  >
) {
  const value = params.value ?? ''
  if (!value) return <EmptyCell />

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    void copyToClipboard(value).then((ok) => {
      if (ok) void message.success('Copied to clipboard')
    })
  }

  return (
    <MonoCopyableText onClick={handleCopy} title={value}>
      {middleEllipsis(value)}
      <CopyOutlined style={{ fontSize: 11 }} />
    </MonoCopyableText>
  )
}

const IN_USE_ENDED_MESSAGE = 'Benefit has ended'
const NOT_IN_USE_MESSAGE = 'Not the current card of any benefit'

/** Amber, not red: the panel already renders "In Use (Ended)" with tone
 * `pending`, and this is a card to clean up, not a failure. */
const EndedIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  color: #faad14;
  font-size: 12px;
`

/**
 * Card Mode's Benefit ID cell. Repeats the card's in-use state inline, next to
 * the `Plan` column that states it outright: the marker rides along with the
 * id it qualifies, so a row reads without crossing the table.
 *
 * | Cell | Means |
 * |---|---|
 * | id | in use |
 * | id + amber clock | on a benefit that has ended |
 * | — | no benefit points at this card |
 *
 * A bare `—` is unambiguous here: in Card Mode the Benefit ID is empty ONLY
 * when the card matches no benefit's VirtualCardKey, so it cannot be misread as
 * missing data.
 */
export function CardBenefitIdCellRenderer(
  params: ICellRendererParams<VcnManagementCardRow, string>
) {
  const benefitId = params.value ?? ''
  const inUse = params.data?.inUse

  if (!benefitId) {
    return (
      <Popover
        content={NOT_IN_USE_MESSAGE}
        trigger="hover"
        placement="top"
        overlayStyle={{ maxWidth: 250 }}
      >
        <span
          role="img"
          aria-label={NOT_IN_USE_MESSAGE}
          style={{ color: MUTED }}
        >
          —
        </span>
      </Popover>
    )
  }

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    void copyToClipboard(benefitId).then((ok) => {
      if (ok) void message.success('Copied to clipboard')
    })
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {/* Truncated the same way the bulk-upload preview truncates benefit IDs:
          a full UUID overflows the column and pushes the ended-benefit icon out
          of view, which is the one thing this cell has to show. `title` keeps
          the whole id available, and copying still yields the full value. */}
      <CopyableText onClick={handleCopy} title={benefitId}>
        {middleEllipsis(benefitId)}
        <CopyOutlined style={{ fontSize: 11 }} />
      </CopyableText>
      {inUse === VcnCardInUseStatus.IN_USE_ENDED ? (
        <Popover
          content={IN_USE_ENDED_MESSAGE}
          trigger="hover"
          placement="top"
          overlayStyle={{ maxWidth: 250 }}
        >
          <EndedIconWrapper role="img" aria-label={IN_USE_ENDED_MESSAGE}>
            <ClockCircleFilled aria-hidden="true" />
          </EndedIconWrapper>
        </Popover>
      ) : null}
    </span>
  )
}

/**
 * The insured person, linked to their family's Coverage Summary.
 *
 * Keyed by `employeePublicKey`, not the person's own key: Coverage Summary is an
 * employee/family page, so a dependent's name correctly lands on the family
 * summary that includes them. Same URL shape and styling the census review uses
 * (`ReviewUpdatesRecordsBase.tsx`).
 *
 * Falls back to plain text when the card has no holder — an orphan card cannot
 * be attributed to anyone, so there is no coverage summary to open.
 */
export function InsuredPersonCellRenderer(
  params: ICellRendererParams<
    VcnManagementBenefitRow | VcnManagementCardRow,
    string
  >
) {
  const name = params.value
  if (!name) return <span style={{ color: MUTED }}>—</span>

  const employeePublicKey = params.data?.employeePublicKey
  if (!employeePublicKey) return <span>{name}</span>

  return (
    <a
      href={`/employees/${employeePublicKey}/coverage`}
      target="_blank"
      rel="noreferrer"
      title="Link to the coverage summary (opens in a new tab)"
      onClick={(e) => e.stopPropagation()}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        color: '#003146',
      }}
    >
      {name}
      <ExportOutlined aria-hidden style={{ fontSize: 11 }} />
    </a>
  )
}

export function getVcnProviderTagColor(provider: string): string {
  switch (provider.trim().toUpperCase()) {
    case VirtualCardType.WEX:
      return 'blue'
    case VirtualCardType.MARQETA:
      return 'purple'
    case 'ISTREAM':
      return 'gold'
    default:
      return 'default'
  }
}

// Provider badge, from P'Sing's `VCN Table.dc.html`: a squarer, bolder chip than
// the status pill — provider is a fixed vocabulary, status is a lifecycle, and
// the two columns sit next to each other. Every property is set here so the
// antd Tag underneath contributes none of its own colour.
const VCN_PROVIDER_PALETTE: Record<
  string,
  { background: string; borderColor: string; color: string }
> = {
  [VirtualCardType.WEX]: colors.provider.wex,
  [VirtualCardType.MARQETA]: colors.provider.marqeta,
}

const VCN_PROVIDER_BADGE_BASE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  fontFamily: font.sans,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: '16px',
  letterSpacing: '0.03em',
  padding: '3px 12px',
  borderRadius: radius.badge,
  borderWidth: 1,
  borderStyle: 'solid',
  margin: 0,
}

export function getVcnProviderTagStyle(provider: string): React.CSSProperties {
  const palette =
    VCN_PROVIDER_PALETTE[provider.trim().toUpperCase()] ??
    colors.provider.neutral
  return { ...VCN_PROVIDER_BADGE_BASE, ...palette }
}

function EmptyCell() {
  return <span style={{ color: MUTED }}>—</span>
}

function TagCell({
  color,
  label,
  style,
}: {
  color: string
  label: string
  style?: React.CSSProperties
}) {
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Tag color={color} style={style}>
        {label}
      </Tag>
    </span>
  )
}

export function VcnProviderCellRenderer(
  params: ICellRendererParams<
    VcnManagementBenefitRow | VcnManagementCardRow,
    string
  >
) {
  const raw = String(params.value ?? '').trim()
  if (!raw) return <EmptyCell />
  return (
    <TagCell
      color={getVcnProviderTagColor(raw)}
      label={raw}
      style={getVcnProviderTagStyle(raw)}
    />
  )
}

export function getPrimaryPaymentMethodTagColor(method: string): string {
  switch (method.trim().toUpperCase()) {
    case 'ACH':
      return 'green'
    case 'VCN':
      return 'cyan'
    case 'GROUP ACH':
      return 'geekblue'
    case 'CHECK':
      return 'gold'
    default:
      return 'default'
  }
}

export function PrimaryPaymentMethodCellRenderer(
  params: ICellRendererParams<
    VcnManagementBenefitRow | VcnManagementCardRow,
    string
  >
) {
  const raw = String(params.value ?? '').trim()
  if (!raw) return <EmptyCell />
  return <TagCell color={getPrimaryPaymentMethodTagColor(raw)} label={raw} />
}

export function VcnStatusCellRenderer(
  params: ICellRendererParams<
    VcnManagementBenefitRow | VcnManagementCardRow,
    VcnCardStatus
  >
) {
  const status = params.value
  if (!status) return <EmptyCell />

  return (
    <span
      onClick={(e) => e.stopPropagation()}
      style={getVcnCardStatusPillStyle(status)}
    >
      {getVcnCardStatusLabel(status)}
    </span>
  )
}

/**
 * Card Mode only — whether a benefit is using this card. Benefit Mode has no
 * equivalent: `VcnManagementBenefitRow` carries no `inUse`.
 *
 * This is the column orphan cleanup scans, so it is a pill rather than text:
 * `Not in use` reads red at a glance across a filtered page.
 */
export function VcnCardInUseCellRenderer(
  params: ICellRendererParams<VcnManagementCardRow, VcnCardInUseStatus>
) {
  const inUse = params.value
  if (!inUse) return <EmptyCell />

  return (
    <span
      onClick={(e) => e.stopPropagation()}
      style={getStatusPillStyle(getVcnCardInUseTone(inUse))}
    >
      {getVcnCardInUseLabel(inUse)}
    </span>
  )
}
